import { GraphSquare, Share } from '@drift-labs/icons';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import {
	PORTFOLIO_PERIOD_TAB_OPTIONS,
	PORTFOLIO_TOOLTIP_COLLISION_PADDING,
} from 'src/components/PortfolioPage/consts';
import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import usePortfolioPerformanceStatsData from 'src/components/PortfolioPage/PortfolioTradeSubPage/hooks/usePortfolioPerformanceStatsData';
import { cj } from 'src/utils/classNames';
import useModal from 'src/components/Modals/hooks/useModal';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';

interface Props {
	period: PortfolioPeriod;
	setPeriod: (period: PortfolioPeriod) => void;
	includeFeesAndFunding: boolean;
	realizedPnlOnly: boolean;
}

export default function PortfolioPerformanceStats({
	period,
	setPeriod,
	includeFeesAndFunding,
	realizedPnlOnly,
}: Props) {
	const currentAccountName = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.name
	);

	const {
		data: {
			pnlChange,
			takerVolume,
			makerVolume,
			maxDrawdown,
			sharpeRatio,
			startOfPeriodTs,
			netFunding,
		},
		isLoading,
		isPlaceholderData,
		isFundingDataLoading,
	} = usePortfolioPerformanceStatsData({
		period,
		includeFeesAndFunding,
		realizedPnlOnly,
	});

	const { show } = useModal();

	const handleSharePnl = () => {
		show({
			type: 'pnl_card',
			props: {
				triggeredFrom: 'portfolio-performance',
				accountName: currentAccountName ?? 'Unnamed Account',
				pnl: pnlChange?.toNum() ?? 0,
				period,
				startOfPeriodTs: startOfPeriodTs ?? 0,
			},
		});
	};

	return (
		<div
			className="flex flex-col gap-y-lg rounded-sm bg-container-bg p-md sm:gap-y-2xl sm:p-2xl"
			data-testid="portfolio-performance-stats"
		>
			<div className="flex flex-wrap items-center justify-between gap-y-md">
				<h2 className="typo-t2 whitespace-nowrap text-text-default">
					Performance Stats
				</h2>
				<Tabs
					size="sm"
					className="not-sm:gap-x-0"
					variant="secondary"
					options={PORTFOLIO_PERIOD_TAB_OPTIONS}
					activeTab={period}
					onTabChange={setPeriod}
					dataTestId="portfolio-period-tabs"
					tabTestIdPrefix="portfolio-period"
				/>
			</div>
			<div className="flex flex-col gap-y-xs">
				<DefinitionTooltip
					definitionId="portfolioPerformancePnlChange"
					className="w-fit typo-b3 text-text-secondary"
					collisionPadding={PORTFOLIO_TOOLTIP_COLLISION_PADDING}
				>
					P&L
				</DefinitionTooltip>
				<div
					// `text-text-default` is applied when account values are hidden.
					className="flex items-center gap-x-sm text-text-default"
				>
					<NumberDisplay
						value={pnlChange}
						type="currency_signed"
						className={cj('typo-t1', isPlaceholderData && 'animate-pulse')}
						skeletonClassName="h-6 w-20"
						loading={isLoading}
						isAccountValueToHide
						signDependentColor
					/>
					<IconButton
						size="lg"
						variant="tertiary"
						className="-m-md p-md"
						icon={Share}
						disabled={isLoading || isPlaceholderData}
						onClick={handleSharePnl}
						dataTestId="portfolio-share-pnl-button"
					/>
				</div>
			</div>
			<div className="flex flex-col gap-y-md typo-b3">
				<div className="flex items-center justify-between">
					<span className="text-text-secondary">Taker Volume</span>
					<NumberDisplay
						value={takerVolume}
						type="currency"
						className={cj(
							'text-text-default',
							isPlaceholderData && 'animate-pulse'
						)}
						skeletonClassName="h-4 w-18"
						loading={isLoading}
						isAccountValueToHide
					/>
				</div>
				<div className="flex items-center justify-between">
					<span className="text-text-secondary">Maker Volume</span>
					<NumberDisplay
						value={makerVolume}
						type="currency"
						className={cj(
							'text-text-default',
							isPlaceholderData && 'animate-pulse'
						)}
						skeletonClassName="h-4 w-20"
						loading={isLoading}
						isAccountValueToHide
					/>
				</div>
				<div className="flex items-center justify-between">
					<DefinitionTooltip
						className="text-text-secondary"
						definitionId="portfolioMaxDrawdown"
					>
						Max Drawdown
					</DefinitionTooltip>
					<NumberDisplay
						value={maxDrawdown}
						type="currency"
						className={cj(
							'text-text-default',
							isPlaceholderData && 'animate-pulse'
						)}
						skeletonClassName="h-4 w-12"
						loading={isLoading}
						isAccountValueToHide
					/>
				</div>
				<div className="flex items-center justify-between">
					<DefinitionTooltip
						className="text-text-secondary"
						definitionId="portfolioSharpeRatio"
					>
						Sharpe Ratio
					</DefinitionTooltip>
					<NumberDisplay
						value={sharpeRatio}
						type="number"
						decimals={2}
						className={cj(
							'text-text-default',
							isPlaceholderData && 'animate-pulse'
						)}
						skeletonClassName="h-4 w-4"
						loading={isLoading}
						isAccountValueToHide
					/>
				</div>
				<div className="flex items-center justify-between">
					<DefinitionTooltip
						className="text-text-secondary"
						definitionId="portfolioNetFunding"
					>
						Net Funding
					</DefinitionTooltip>
					<div className="flex items-center gap-x-xs">
						<NumberDisplay
							value={netFunding}
							type="currency"
							className={cj(
								'text-text-default',
								isPlaceholderData && 'animate-pulse'
							)}
							skeletonClassName="h-4 w-16"
							loading={isFundingDataLoading}
							isAccountValueToHide
						/>
						<IconButton
							size="lg"
							variant="tertiary"
							className="-m-md p-md"
							icon={GraphSquare}
							onClick={() =>
								show({
									type: 'net_funding_history',
									props: { defaultPeriod: period },
								})
							}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
